/**
 * MatchCard — single-row list item for the Matches page.
 *
 * Source: design_handoff_tank/screens-matches.jsx § Screen03Matches
 * (and screens-matches.jsx § MatchCard simple variant).
 *
 * Layout (3 lines, no color bar / no status-mix bar / no avatar row):
 *   line 1: Home vs Away (or event name)         · status pill (right)
 *   line 2: time mono · competition · round · venue
 *   line 3: N photos · editor.username · M destinations
 */

import clsx from 'clsx';
import {
  formatDateTime,
  getMatchStatus,
} from '../../utils/dateUtils';
import { getCompetitionLabel } from '../../utils/constants';
import { StatusPill } from '../../../../shared/components/redesign';
import styles from './MatchCard.module.css';

function matchStatusKind(status) {
  switch (status) {
    case 'live':
      return 'attention';
    case 'upcoming':
      return 'pending';
    case 'past':
      return 'exported';
    case 'cancelled':
      return 'skipped';
    default:
      return 'pending';
  }
}

const MATCH_STATUS_LABELS = {
  upcoming: 'Upcoming',
  live: 'Live',
  past: 'Finished',
  cancelled: 'Cancelled',
};

/** Pick a single representative crew member for the dense footer line. */
function pickCrewLabel(photographers = [], editors = []) {
  // Prefer a non-NULL editor (matches the design caption "yannick.verhoeven").
  for (const e of editors) {
    if (e?.user?.username) return e.user.username;
  }
  for (const p of photographers) {
    if (p?.user?.username) return p.user.username;
  }
  return null;
}

export default function MatchCard({
  match,
  selected = false,
  // Backwards-compat: callers using `isSelected` keep working.
  isSelected,
  onClick,
}) {
  const status = getMatchStatus(match);
  const sel = selected || isSelected;
  const photographers = match.photographer_assignments || [];
  const editors = match.editor_assignments || [];
  const photoCount = match.photo_count || 0;
  const destCount = match.ftp_destination_count || 0;
  const crewLabel = pickCrewLabel(photographers, editors);

  const handleClick = () => onClick?.(match);
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <article
      className={clsx(styles.card, sel && styles.selected)}
      role="button"
      tabIndex={0}
      data-match-card=""
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/* Line 1: teams + status pill */}
      <div className={styles.row1}>
        <div className={styles.teams}>
          {match.home_team_name && match.away_team_name ? (
            <>
              <span className={styles.teamHome}>{match.home_team_name}</span>
              <span className={styles.vs}>vs</span>
              <span className={styles.teamAway}>{match.away_team_name}</span>
            </>
          ) : (
            <span className={styles.eventName}>
              {match.name || 'Untitled Event'}
            </span>
          )}
        </div>
        <StatusPill kind={matchStatusKind(status)} compact>
          {MATCH_STATUS_LABELS[status] || status}
        </StatusPill>
      </div>

      {/* Line 2: time · competition · round · venue */}
      <div className={styles.row2}>
        <span className={styles.time}>
          {formatDateTime(match.start_time, { year: undefined })}
        </span>
        {match.competition && (
          <>
            <span className={styles.dot}>·</span>
            <span>{getCompetitionLabel(match.competition)}</span>
          </>
        )}
        {match.round != null && match.round !== '' && (
          <>
            <span className={styles.dot}>·</span>
            <span>Round {match.round}</span>
          </>
        )}
        {match.venue && (
          <>
            <span className={styles.dot}>·</span>
            <span className={styles.venue}>{match.venue}</span>
          </>
        )}
      </div>

      {/* Line 3: N photos · crewLabel · M destinations */}
      <div className={styles.row3}>
        <span className={styles.photoCount}>
          {photoCount} {photoCount === 1 ? 'photo' : 'photos'}
        </span>
        {crewLabel && (
          <>
            <span className={styles.dot}>·</span>
            <span className={styles.crew}>{crewLabel}</span>
          </>
        )}
        {destCount > 0 && (
          <>
            <span className={styles.dot}>·</span>
            <span>
              {destCount} {destCount === 1 ? 'destination' : 'destinations'}
            </span>
          </>
        )}
      </div>
    </article>
  );
}
